<template>
	<div>
		<div class="row py-3 ws-border-last-contrast">
			<div class="col-5 d-flex justify-content-start align-items-center actions">
				<a
					v-if="currentWorkspace"
					@click.prevent.stop="toggleDocumentSidebar"
					v-b-tooltip.hover
					:title="documentSidebar ? 'Close Document Sidebar' : 'Open Document Sidebar'"
					:class="{ active: documentSidebar, 'with-notifications': workspaceDocuments.length }"
					:data-files-count="workspaceDocuments.length"
				>
					<font-awesome-icon :icon="['far', 'folder-open']" v-if="documentSidebar" />
					<font-awesome-icon :icon="['far', 'folder']" v-if="!documentSidebar" />
				</a>
				<a
					v-if="currentWorkspace"
					@click.prevent.stop="togglePreview"
					:disabled="isAnnotating || isPageOrderable"
					v-b-tooltip.hover
					:title="isPreviewEnabled ? 'Close Thumbnail Sidebar' : 'Open Thumbnail Sidebar'"
					:class="{ active: isPreviewEnabled }"
				>
					<i class="mdi mdi-view-list"></i>
				</a>
				<a
					v-if="currentWorkspace"
					@click.prevent.stop="toggleDoubleView"
					:disabled="isAnnotating || isPageOrderable"
					:class="{ active: doubleEnabled }"
					v-b-tooltip.hover
					:title="doubleEnabled ? 'View Single Page' : 'View Side By Side'"
				>
					<font-awesome-layers class="fa-fw">
						<font-awesome-icon :icon="['far', 'file-alt']" transform="shrink-3 left-5" />
						<font-awesome-icon :icon="['far', 'file-alt']" transform="shrink-3 right-5" />
					</font-awesome-layers>
				</a>
				<a
					v-if="currentWorkspace"
					@click.prevent.stop="togglePageOrder"
					:disabled="isAnnotating"
					v-b-tooltip.hover
					title="Reorder/Remove Pages"
					:class="{ active: isPageOrderable }"
				>
					<font-awesome-layers class="fa-fw">
						<font-awesome-icon :icon="['fas', 'reply']" transform="shrink-9 down-8 right-7" rotation="90" />
						<font-awesome-icon :icon="['far', 'copy']" />
						<font-awesome-icon :icon="['fas', 'reply']" transform="shrink-11 up-8 left-7" rotation="270" />
					</font-awesome-layers>
				</a>
				<a
					v-if="currentWorkspace"
					:disabled="isPageOrderable"
					@click.prevent.stop="toggleAnnotation"
					v-b-tooltip.hover
					:title="!isAnnotating ? 'Enter Edit Mode' : 'Use the exit button to save and exit Edit mode'"
					:class="{ active: isAnnotating }"
				>
					<font-awesome-icon :icon="['far', 'edit']" />
				</a>
				<a
					v-if="currentWorkspace && isAnnotating"
					class="btn btn-light-blue"
					@click="showStampSelectionModal = true"
					v-b-tooltip.hover
					title="Add Stamp"
				>
					<font-awesome-icon :icon="['fas', 'stamp']" />
				</a>
				<a
					v-if="currentWorkspace && isAnnotating"
					@click.prevent.stop="$bus.$emit('pdf::annotation::suggestions::toggle', true)"
					v-b-tooltip.hover
					title="Show possible signature areas"
					:class="{ active: showSuggestions }"
				>
					<font-awesome-icon :icon="['fas', 'file-signature']" />
				</a>
			</div>
			<div class="col-7 d-flex justify-content-end align-items-center actions">
				<a v-if="currentWorkspace" @click.prevent.stop="reloadDocumentList" v-b-tooltip.hover title="Refresh Document List">
					<font-awesome-icon :icon="['fas', 'sync']" />
				</a>

				<el-select
					v-model="preSelectedDocument"
					filterable
					:placeholder="documentsLoaded ? `Select Document` : `Fetching Documents..`"
					class="w-25"
					v-b-tooltip.hover
					title="Include selected document"
					popper-class="document-types"
				>
					<div v-if="!_.isEmpty(allDocs)" class="title">APPLICATION DOCUMENTS</div>

					<el-collapse :key="`${group}-application`" v-for="group in allDocs.keys()" accordion>
						<el-collapse-item :title="group" :name="group">
							<el-option
								v-for="item in allDocs.get(group)"
								v-if="item.file_name.split('.').pop() !== 'docx'"
								:key="item.id"
								:label="`${item.name} (${item.file_name})`"
								:value="`${item.id}_application`"
								style="height: auto !important"
								:disabled="workspaceDocumentIds.includes(item.id)"
							>
								<span style="display: block; padding-top: 0.5rem">{{ item.name }}</span>
								<span style="display: block; font-size: 10px; line-height: 12px; padding-bottom: 1rem">{{ item.file_name }}</span>
							</el-option>
						</el-collapse-item>
					</el-collapse>

					<div v-if="!_.isEmpty(libraryDocuments)" class="title">LIBRARY DOCUMENTS</div>

					<el-collapse :key="`${group}-library`" v-for="group in libraryDocuments.keys()" accordion>
						<el-collapse-item :title="group" :name="group">
							<el-option
								v-for="item in libraryDocuments.get(group)"
								:key="item.id"
								:label="`${item.name} (${item.file_name})`"
								:value="`${item.id}_library`"
								:disabled="workspaceDocumentIds.includes(item.id)"
							></el-option>
						</el-collapse-item>
					</el-collapse>
				</el-select>

				<a class="btn-green ml-2 icon with-text" @click.prevent.stop="addDocumentToWorkspace" :disabled="!preSelectedDocument">
					Add Document
				</a>
                <file-upload
                    class="action"
                    :modal="true"
                    :endpoint="`/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.currentWorkspace}/documents`"
                    :backend="true"
                    :loading="uploading"
                    :circle="true"
                    label="Drop documents here to upload <b><em>or</em></b> <span style='text-decoration: underline;'>click to browse</span>"
                    :icon="true"
                    @uploadSuccess="uploadComplete()"
                />
				<!-- <a
					class="btn-light-blue btn ml-2 icon"
					v-b-tooltip.hover
					title="Insert Image (PNG, JPG, or JPEG) or PDF as new page"
					@click.prevent.stop="showDocumentUploadModal = true"
				>
					<font-awesome-icon :icon="['fas', 'file-upload']" />
				</a> -->
			</div>
		</div>
		<div class="row">
			<div class="col-2" :class="documentSidebar ? `d-block` : `d-none`" style="background-color: #545a69">
				<div class="row pt-3 mx-1 document-sidebar">
					<div
						v-for="(workspaceDocument, index) in workspaceDocuments"
						:key="workspaceDocument.id"
						style="background: #2493ef; border-radius: 5px"
						class="mb-2 col-12 sidebar-document p-2"
					>
						<button
							@click="removeWorkspaceDocument(workspaceDocument.id, index)"
							class="amb-icon-btn"
							title="Remove entire document from bundle."
						>
							<i class="fas fa-times-circle text-white"></i>
						</button>
						<span class="text-white" style="display: block">{{ workspaceDocument.name }}</span>
						<span class="text-white" style="display: block; font-size: 10px; line-height: 12px; text-overflow: ellipsis">
							{{ workspaceDocument.filename }}
						</span>
					</div>
				</div>
			</div>
			<slot></slot>
		</div>
		<x-modal
			class="slim-modal"
			:loading="uploading"
			width="500"
			v-if="showDocumentUploadModal"
			@close="showDocumentUploadModal = false"
			@cancel="showDocumentUploadModal = false"
			@ok="submitSlimForm"
		>
			<div slot="title" :class="`d-flex justify-content-between align-items-center`">Upload Document</div>
			<div>
				<slim-cropper :submitPressed="slimSubmit" default-input-name="file" @upload="slimSubmit = false" :options="slimOptions">
					<input type="file" v-show="false" accept="image/jpeg, image/png, application/pdf" />
				</slim-cropper>
			</div>
		</x-modal>
		<x-modal width="300" v-if="showStampSelectionModal" @close="showStampSelectionModal = false" :footer="false">
			<div slot="title" :class="`d-flex justify-content-between align-items-center`">Select Stamp</div>
			<div class="row" style="overflow-y: scroll; max-height: 500px">
				<div
					class="col-12"
					v-for="io in imageObjects"
					:key="io.id"
					@click="addImage(io, imagePageNumber ? imagePageNumber : currentPage)"
					style="cursor: pointer; text-align: center"
				>
					<img :src="io.temporary_url" class="img-fluid mb-3" />
				</div>
			</div>
		</x-modal>
	</div>
</template>

<script>
	import { ulid } from "ulid"
	import { mapGetters } from "vuex"

	function groupBy(list, keyGetter) {
		const map = new Map()
		list.forEach((item) => {
			const key = keyGetter(item)
			const collection = map.get(key)
			if (!collection) {
				map.set(key, [item])
			} else {
				collection.push(item)
			}
		})

		return map
	}

	export default {
		name: "PDFWorkspace",
		components: {},
		props: {
			currentWorkspace: {
				type: Number,
			},
			allDocs: {
				type: Map,
			},
			docs: {
				type: Array,
			},
			headers: {
				type: Object,
			},
			workspaceDocuments: {
				type: Array,
			},
			pageCount: {
				type: Number,
			},
			isPreviewEnabled: {
				type: Boolean,
			},
			isPageOrderable: {
				type: Boolean,
			},
			doubleEnabled: {
				type: Boolean,
			},
			isAnnotating: {
				type: Boolean,
			},
			documentSidebar: {
				type: Boolean,
			},
			imageObjects: {
				required: true,
			},
			currentPage: {
				required: true,
			},
			showSuggestions: {
				type: Boolean,
			},
		},
		data() {
			return {
				selectedDocument: null,
				preSelectedDocument: null,
				loadingAddDocument: false,
				waitingUpload: false,
				documentsLoaded: false,
				showDocumentUploadModal: false,
				loadingAddDocument: false,
				openStamps: false,
				showStampSelectionModal: false,
				imagePageNumber: null,
				clickedArea: null,
				uploading: false,
				slimSubmit: false,
				slimOptions: {
					initialImage: "",
					jpegCompression: 30,
					service: this.slimService,
					didPDF: this.syncFile,
					dataRatio: "free",
					buttonEditLabel: '<i class="fas fa-check-circle"></i>',
					instantEdit: true,
					post: "output",
					forceType: "jpg",
					label: 'Drop files here <b><em>or</em></b> <span style="text-decoration: underline;">click to browse</span>',
					size: "2480, 3508",
					meta: {
                        document_location: 'application'
                    },
				},
			}
		},
		computed: {
			...mapGetters({
				libDocuments: "documentLibrary/getAllDocuments",
			}),
			workspaceDocumentIds() {
				return this.workspaceDocuments.map((wd) => {
					return wd.parent_document_id ? wd.parent_document_id : wd.id
				})
			},
			libraryDocuments() {
				if (!_.isEmpty(this.libDocuments)) {
					var libAttachments = new Map()
					libAttachments = groupBy(this.libDocuments, (doc) => {
						return (
							doc.document_type && doc.document_category != "markup" ? doc.document_type.document_type || "Other" : "Other"
						).toUpperCase()
					})
					return libAttachments
				}
				return []
			},
		},
		watch: {
			showDocumentUploadModal(newVal, oldVal) {
				if (newVal == false) {
					this.slimOptions.initialImage = ""
				}
			},
		},
		mounted() {
			this.$bus.$on("pdf::annotations::stamp::show", this.toggleStampModal)
			this.$bus.$on("pdf::contextmenu::clickedarea", (e) => {
				this.clickedArea = e
			})
			this.$bus.$on("pdf::annotations::text::add", this.addText)
			this.$bus.$on("pdf::docs::loaded", this.documentsListLoaded)
		},
		updated() {},
		beforeDestroy() {
			this.$bus.$off("pdf::annotations::stamp::show")
			this.$bus.$off("pdf::contextmenu::clickedarea")
			this.$bus.$off("pdf::annotations::text::add")
			this.$bus.$off("pdf::docs::loaded")
		},
		created() {},
		methods: {
			togglePreview() {
				this.$bus.$emit("pdf::toggle::preview", true)
			},
			toggleDoubleView() {
				this.$bus.$emit("pdf::toggle::doubleview", true)
			},
			togglePageOrder() {
				this.$bus.$emit("pdf::toggle::page::order", true)
			},
			toggleAnnotation() {
				if (this.isAnnotating) return
				this.$bus.$emit("pdf::toggle::page::edit", true)
			},
			toggleDocumentSidebar() {
				this.$bus.$emit("pdf::toggle::documentsidebar", true)
			},
			toggleStampModal(pageNumber) {
				this.showStampSelectionModal = true
				this.imagePageNumber = pageNumber
			},
			submitSlimForm() {
				this.slimSubmit = true
			},

			// Annotation Methods
			// Save and Delete methods
			addText(page_num) {
				let object = {
					id: ulid(),
					page_num: page_num,
					x: this.clickedArea != null ? this.clickedArea.offsetX : 0,
					y: this.clickedArea != null ? this.clickedArea.offsetY : 0,
					type: "TEXT",
					width: 200,
					height: 20,
					applicant_id: null,
					editted: false,
					object_id: null,
					text: "",
					new: true,
					styles: "",
					styleOptions: {
						color: "black",
						chosenSize: 14,
						textStyle: {
							bold: false,
							italic: false,
							underline: false,
						},
						fontSize: {
							twelve: false,
							forteen: false,
							sixteen: false,
							eighteen: false,
						},
					},
				}
				this.$bus.$emit("pdf::annotation::add", object)
			},

			addImage(annotation, page_num) {
				let object = {
					id: ulid(),
					page_num: page_num,
					x: this.clickedArea != null ? this.clickedArea.offsetX : 0,
					y: this.clickedArea != null ? this.clickedArea.offsetY : 0,
					width: annotation.width,
					applicant_id: null,
					height: annotation.height,
					type: "IMAGE",
					object_id: annotation.id,
					src: annotation.temporary_url,
					new: true,
				}
				this.$bus.$emit("pdf::annotation::add", object)
				this.showStampSelectionModal = false
			},
			addPage() {
				this.$refs.file_holder_new_page.click()
			},
			documentsListLoaded() {
				this.documentsLoaded = true
			},
			reloadDocumentList() {
				this.documentsLoaded = false
				this.$bus.$emit("pdf::docs::reload", true)
			},
			async addDocumentToWorkspace() {
				let selectedArray = this.preSelectedDocument.split("_")

				this.selectedDocument = selectedArray[0]
				let selectedType = selectedArray[1]
				let docLibrary = selectedType == "library" ? this.libDocuments : this.docs

				const selectedItem = docLibrary.findIndex((item) => {
					return item.id == this.selectedDocument
				})

				if (docLibrary[selectedItem].url.match(/.(jpg|jpeg|png|gif)$/i)) {
					this.slimOptions.meta.parent_document_id = docLibrary[selectedItem].id
                    this.slimOptions.meta.document_location = selectedType
					this.slimOptions.initialImage = docLibrary[selectedItem].temporary_url
					this.showDocumentUploadModal = true
					return
				}

				try {
					this.loadingAddDocument = true
					let res = await axios
						.post(
							`/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.currentWorkspace}/documents`,
							{
								document_ids: this.selectedDocument,
								document_location: selectedType,
							},
							this.headers
						)
						.then((res) => {
							this.$bus.$emit("pdf::new::loading", true)
							this.$bus.$emit("pdf::add", true)
							this.preSelectedDocument = null
							this.loadingAddDocument = false
						})
						.finally((f) => {
							this.$message({
								message: "Document Added to Workspace",
								type: "success",
							})
						})
				} catch (err) {
					this.loadingAddDocument = false
					console.error(err)
				}
			},

			/**
			 * to remove document from workspace
			 * @param {Number} id - document id
			 * @param {Number} index - document pos
			 */
			async removeWorkspaceDocument(id, index) {
				try {
					let res = await axios.delete(
						`/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.currentWorkspace}/documents/${id}`,
						this.headers
					)
					this.workspaceDocuments.splice(index, 1)
					this.$bus.$emit("pdf::remove", id)
				} catch (err) {
					console.error(err)
				}
			},

			async syncFile(file) {
				this.loadingAddDocument = true
				try {
					let form = new FormData()
					form.append("file", file)
					this.waitingUpload = true
					let res = await axios
						.post(
							`/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.currentWorkspace}/documents`,
							form,
							{
								headers: {
									Authorization: `Bearer ${this.$store.getters.token}`,
									"Content-Type": "multipart/form-data",
								},
							}
						)
						.then((res) => {
							this.waitingUpload = false
							this.$bus.$emit("pdf::new::loading", true)
							this.$bus.$emit("pdf::add", true)

							this.preSelectedDocument = null
							this.loadingAddDocument = false
							this.showDocumentUploadModal = false
						})
				} catch (Err) {
					console.error(Err)
					this.waitingUpload = false
				}
			},

			async slimService(formdata, progress, success, failure, slim) {
				const config = {
					onUploadProgress: (e) => {
						progress(e.loaded, e.total)
					},
					headers: {
						Authorization: `Bearer ${this.$store.getters.token}`,
						"Content-Type": "multipart/form-data",
					},
				}
				this.uploading = true
				this.loadingAddDocument = true
				try {
					this.waitingUpload = true
					let res = await axios
						.post(
							`/api/v2/applications/${this.$store.getters.initAppData.id}/workspaces/${this.currentWorkspace}/documents`,
							formdata,
							config
						)
						.then((res) => {
							this.waitingUpload = false
							this.$bus.$emit("pdf::new::loading", true)
							this.$bus.$emit("pdf::add", true)

							this.preSelectedDocument = null
							this.loadingAddDocument = false
							this.showDocumentUploadModal = false
							this.slimOptions.initialImage = ""
							this.slimOptions.meta.parent_document_id = ""
							this.slimOptions.meta.document_location = "application"
							this.uploading = false
						})
				} catch (Err) {
					console.error(Err)
					this.uploading = false
					this.waitingUpload = false
				}
			},

            uploadComplete(){
                this.waitingUpload = false
                this.$bus.$emit("pdf::new::loading", true)
                this.$bus.$emit("pdf::add", true)

                this.preSelectedDocument = null
                this.loadingAddDocument = false
                this.showDocumentUploadModal = false
                this.slimOptions.initialImage = ""
                this.slimOptions.meta.parent_document_id = ""
                this.slimOptions.meta.document_location = "application"
                this.uploading = false
            }
		},
	}
</script>

<style lang="scss" scoped>
	// @import '@assets/sass/backend/_core-variables.scss';
	.sidebar-document {
		position: relative;
		.amb-icon-btn {
			position: absolute;
			top: 0px;
			right: 0px;
		}
	}

	.el-select-dropdown__item.is-disabled {
		cursor: not-allowed !important;
		background: #5bbc2a !important;
		color: #fff !important;

		&:hover {
			cursor: not-allowed !important;
			background: #5bbc2a !important;
			color: #fff !important;
		}
	}

	.actions {
		a {
			border-radius: 5px !important;
			padding: 5px;
			border: 1px solid #d3d3d3;
			font-size: 1.5rem;
			color: #a0a0a0;
			height: 40px;
			width: 44px;
			display: flex;
			align-items: center;
			justify-content: center;
			margin-right: 4px;

			.jcc {
				display: flex;
				align-items: center;
				justify-content: center;
			}

			&:hover {
				color: #fff;
				background: #5bbc2a;
			}

			&.active {
				color: #fff;
				background: #2493ef;
			}

			&.with-notifications {
				position: relative;

				&:after {
					content: attr(data-files-count);
					opacity: 1;
					position: absolute;
					background: rgb(221, 52, 69);
					height: 20px;
					top: -10px;
					right: -10px;
					width: 20px;
					text-align: center;
					line-height: 20px;
					font-size: 10px;
					border-radius: 50%;
					color: white;
					font-weight: 600;
					border: 1px solid #ffffff;
					z-index: 1;
				}
			}
		}
	}
	a {
		&.icon {
			font-size: 1.8rem;
			line-height: 0;
			width: 40px;
			height: 40px;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-wrap: nowrap;

			&[disabled] {
				background-color: #c0c4cc !important;
			}
		}

		&.with-text {
			width: 160px !important;
			font-size: 0.8rem;
		}
	}
</style>
<style lang="scss">
	.document-types {
		.el-select-dropdown__list {
			padding: 0rem;

			.el-select-dropdown__item {
				font-size: 12px;
				padding: 0 10px;
				position: relative;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				color: #606266;
				height: 30px;
				line-height: 30px;
				box-sizing: border-box;
				cursor: pointer;
			}

			.el-collapse-item__header {
				border-bottom: 1px solid #fbfbfb;
				padding-left: 0.5rem;
			}
		}
	}

	.document-sidebar {
		overflow-y: scroll;
		max-height: calc(100vh - 300px);
		-ms-overflow-style: none;

		&::-webkit-scrollbar {
			display: none;
		}
	}

	.slim-btn {
		font-size: 1em;
		background-image: none;
	}

	.slim-modal {
		.modal-body {
			overflow: hidden;
			overflow-y: scroll;
			max-height: 65vh;
			-ms-overflow-style: none;

			&::-webkit-scrollbar {
				display: none;
			}
		}
	}
</style>
